import * as React from 'react';
import type { LinksFunction, LoaderFunctionArgs } from '@remix-run/node';
import {
  Links,
  Meta,
  Outlet,
  Scripts,
  ScrollRestoration,
  useFetchers,
  useRouteLoaderData,
} from '@remix-run/react';
import { Toaster } from '@se-platform/ui/sonner';
import { DEFAULT_THEME, THEME_RESOURCE_PATH, type Theme, themeSchema } from './theme';
import { getTheme } from './theme.server';
import './tailwind.css';

const DARK_CLASS = 'dark';
const DARK_SCHEME_QUERY = '(prefers-color-scheme: dark)';

// Runs in <head> before the body paints when the preference is `system`, so a dark
// operating system never sees a light frame first. Hydration takes over from here.
const SYSTEM_THEME_SCRIPT = `document.documentElement.classList.toggle('${DARK_CLASS}', window.matchMedia('${DARK_SCHEME_QUERY}').matches);`;

export const links: LinksFunction = () => [
  { rel: 'preconnect', href: 'https://fonts.googleapis.com' },
  {
    rel: 'preconnect',
    href: 'https://fonts.gstatic.com',
    crossOrigin: 'anonymous',
  },
  {
    // Roboto, in the marketplace app's four weights, is the base face: the design
    // system's preset puts it on <html>, exactly as in the marketplace. Montserrat 200
    // (standing in for Graphie ExtraLight) and Lato are the web brand's voices, which
    // only the way-in screens opt into through their shell.
    rel: 'stylesheet',
    href: 'https://fonts.googleapis.com/css2?family=Roboto:wght@300;400;500;700&family=Montserrat:wght@200;300;600;700&family=Lato:wght@400;700&display=swap',
  },
];

export async function loader({ request }: LoaderFunctionArgs) {
  return { theme: await getTheme(request) };
}

export function Layout({ children }: { children: React.ReactNode }) {
  const theme = useTheme();
  useDarkClass(theme);

  return (
    // The inline script and the effect both write the class outside React's render.
    <html lang="en" className={theme === 'dark' ? DARK_CLASS : undefined} suppressHydrationWarning>
      <head>
        <meta charSet="utf-8" />
        <meta name="viewport" content="width=device-width, initial-scale=1" />
        {theme === 'system' ? (
          <script dangerouslySetInnerHTML={{ __html: SYSTEM_THEME_SCRIPT }} />
        ) : null}
        <Meta />
        <Links />
      </head>
      <body>
        {children}
        <Toaster richColors position="top-center" theme={theme} />
        <ScrollRestoration />
        <Scripts />
      </body>
    </html>
  );
}

export default function App() {
  return <Outlet />;
}

/**
 * The preference in force: a choice still being posted wins over the stored one, so the
 * page repaints on the click rather than after the round trip. The error boundary renders
 * this layout without loader data, and falls back to the default.
 */
function useTheme(): Theme {
  const data = useRouteLoaderData<typeof loader>('root');
  const pending = useFetchers().find(
    (currentFetcher) => currentFetcher.formAction === THEME_RESOURCE_PATH,
  );
  const pendingTheme = themeSchema.safeParse(pending?.formData?.get('theme'));
  if (pendingTheme.success) {
    return pendingTheme.data;
  }
  return data?.theme ?? DEFAULT_THEME;
}

/**
 * Keeps the `dark` class on <html> in step with the preference after hydration: pinned
 * for light and dark, and following the operating system's setting while it is `system`.
 */
function useDarkClass(theme: Theme) {
  React.useEffect(() => {
    const root = document.documentElement;
    if (theme !== 'system') {
      root.classList.toggle(DARK_CLASS, theme === 'dark');
      return undefined;
    }

    const darkScheme = window.matchMedia(DARK_SCHEME_QUERY);
    const applySystemScheme = () => root.classList.toggle(DARK_CLASS, darkScheme.matches);
    applySystemScheme();
    darkScheme.addEventListener('change', applySystemScheme);
    return () => darkScheme.removeEventListener('change', applySystemScheme);
  }, [theme]);
}
